import { useCallback, useEffect, useState } from 'react';
import { Eye, Search, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import {
  analyticsService,
  type ProductViewSort,
  type ProductViewStat,
  type ProductViewerRow,
  type ProductViewerDetail,
  type ViewerProductDetail,
} from '../services/analyticsService';
import { formatDateTime, formatJalaliDate } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type Tab = 'products' | 'viewers';

const sortOptions: { value: ProductViewSort; label: string }[] = [
  { value: 'most_views', label: 'بیشترین مشاهده' },
  { value: 'least_views', label: 'کمترین مشاهده' },
  { value: 'newest', label: 'جدیدترین' },
  { value: 'oldest', label: 'قدیمی‌ترین' },
];

export function ProductViewsPage() {
  const [tab, setTab] = useState<Tab>('products');
  const [sort, setSort] = useState<ProductViewSort>('most_views');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductViewStat[]>([]);
  const [viewers, setViewers] = useState<ProductViewerRow[]>([]);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailRows, setDetailRows] = useState<(ProductViewerDetail | ViewerProductDetail)[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const itemsPerPage = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'products') {
        const res = await analyticsService.listProductViews({ page, limit: itemsPerPage, sort });
        setProducts(res.data ?? []);
        setTotalPages(Math.max(1, res.totalPages));
      } else {
        const res = await analyticsService.listProductViewers({ page, limit: itemsPerPage, sort });
        setViewers(res.data ?? []);
        setTotalPages(Math.max(1, res.totalPages));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت بازدیدها');
      setProducts([]);
      setViewers([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    setDetailTitle('');
    setDetailRows([]);
  }, [tab, sort]);

  const filteredProducts = products.filter((p) => {
    if (!searchTerm) return true;
    const q = searchTerm.trim();
    return (
      (p.name ?? '').includes(q) ||
      (p.sku ?? '').includes(q) ||
      (p.slug ?? '').includes(q)
    );
  });

  const filteredViewers = viewers.filter((v) => {
    if (!searchTerm) return true;
    const q = searchTerm.trim();
    return (
      (v.customerName ?? '').includes(q) ||
      (v.customerPhone ?? '').includes(q) ||
      (v.visitorKey ?? '').includes(q)
    );
  });

  const openProductViewers = async (p: ProductViewStat) => {
    setDetailLoading(true);
    setDetailTitle(`بازدیدکنندگان «${p.name}»`);
    try {
      const res = await analyticsService.listViewersForProduct(p.productId, {
        page: 1,
        limit: 50,
        sort: 'newest',
      });
      setDetailRows(res.data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در جزئیات');
      setDetailRows([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openViewerProducts = async (v: ProductViewerRow) => {
    setDetailLoading(true);
    setDetailTitle(`محصولات دیده‌شده توسط ${v.customerName}`);
    try {
      const res = await analyticsService.listProductsForViewer(v.visitorKey, {
        page: 1,
        limit: 50,
        sort: 'newest',
      });
      setDetailRows(res.data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در جزئیات');
      setDetailRows([]);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">بازدید محصولات</h1>
        <p className="text-slate-600 mt-1">کدام محصول دیده شده و کدام کاربر چه محصولاتی را دیده</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setTab('products')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
            tab === 'products' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-700'
          }`}
        >
          <Eye className="h-4 w-4" />
          بر اساس محصول
        </button>
        <button
          type="button"
          onClick={() => setTab('viewers')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
            tab === 'viewers' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-700'
          }`}
        >
          <Users className="h-4 w-4" />
          بر اساس کاربر
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder={tab === 'products' ? 'جستجو نام / SKU...' : 'جستجو نام / تلفن...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as ProductViewSort)}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-right text-sm font-semibold">ردیف</th>
                {tab === 'products' ? (
                  <>
                    <th className="px-4 py-3 text-right text-sm font-semibold">محصول</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">بازدید ثبت‌شده</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">بازدیدکننده یکتا</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">آخرین بازدید</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">جزئیات</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 text-right text-sm font-semibold">کاربر / بازدیدکننده</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">تلفن</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">تعداد بازدید</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">تعداد محصول</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">آخرین بازدید</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">جزئیات</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : tab === 'products' ? (
                filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      هنوز بازدید محصولی ثبت نشده (پس از مهاجرت و بازدید جدید ظاهر می‌شود)
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((p, index) => (
                    <tr key={p.productId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {rowNumber(index, page, itemsPerPage)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.sku}</p>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">
                        {(p.viewCount ?? 0).toLocaleString('fa-IR')}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">
                        {(p.uniqueVisitors ?? 0).toLocaleString('fa-IR')}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(p.lastViewedAt)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => void openProductViewers(p)}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          چه کسانی دیدند
                        </button>
                      </td>
                    </tr>
                  ))
                )
              ) : filteredViewers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    هنوز بازدیدکننده‌ای ثبت نشده
                  </td>
                </tr>
              ) : (
                filteredViewers.map((v, index) => (
                  <tr key={v.visitorKey} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {rowNumber(index, page, itemsPerPage)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{v.customerName}</td>
                    <td className="px-4 py-3 text-sm text-slate-600" dir="ltr">
                      {v.customerPhone || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      {(v.viewCount ?? 0).toLocaleString('fa-IR')}
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      {(v.productCount ?? 0).toLocaleString('fa-IR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(v.lastViewedAt)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openViewerProducts(v)}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        چه محصولاتی
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-slate-100">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      {(detailTitle || detailLoading) && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-slate-900">{detailTitle || 'جزئیات'}</h2>
            <button
              type="button"
              className="text-sm text-slate-500"
              onClick={() => {
                setDetailTitle('');
                setDetailRows([]);
              }}
            >
              بستن
            </button>
          </div>
          {detailLoading ? (
            <p className="text-sm text-slate-500 py-6 text-center">در حال بارگذاری...</p>
          ) : detailRows.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">موردی نیست</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {detailRows.map((row, i) => {
                if ('customerName' in row) {
                  return (
                    <li key={`${row.visitorKey}-${i}`} className="py-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{row.customerName}</p>
                        <p className="text-xs text-slate-500" dir="ltr">
                          {row.customerPhone || row.visitorKey}
                        </p>
                      </div>
                      <div className="text-left text-xs text-slate-500">
                        <p>{formatJalaliDate(row.visitDate)}</p>
                        <p>{formatDateTime(row.viewedAt)}</p>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={`${row.productId}-${i}`} className="py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{row.name}</p>
                      <p className="text-xs text-slate-500">{row.sku}</p>
                    </div>
                    <div className="text-left text-xs text-slate-500">
                      <p>{formatJalaliDate(row.visitDate)}</p>
                      <p>{formatDateTime(row.viewedAt)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
